import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';

const InteractiveParticles = () => {
  const canvasRef = useRef(null);
  const { darkMode } = useTheme();

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    const mouse = { x: null, y: null };

    const COUNT      = 68;
    const LINE_DIST  = 130;
    const CURSOR_DIST = 165;
    const REPEL_DIST  = 105;

    const setSize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    setSize();

    const ro = new ResizeObserver(setSize);
    ro.observe(canvas);

    const particles = Array.from({ length: COUNT }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.55,
      vy: (Math.random() - 0.5) * 0.55,
      r:  Math.random() < 0.7
            ? Math.random() * 1   + 0.8
            : Math.random() * 1.5 + 1.5,
    }));

    const getAccent = () =>
      getComputedStyle(document.documentElement)
        .getPropertyValue('--accent').trim() || '#6366f1';

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      const color = getAccent();

      ctx.clearRect(0, 0, W, H);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        p.x += p.vx;
        p.y += p.vy;

        // wrap edges
        if (p.x < -12) p.x = W + 12;
        else if (p.x > W + 12) p.x = -12;
        if (p.y < -12) p.y = H + 12;
        else if (p.y > H + 12) p.y = -12;

        // repel from cursor
        if (mouse.x !== null) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const d  = Math.hypot(dx, dy);
          if (d < REPEL_DIST && d > 0) {
            const f = ((REPEL_DIST - d) / REPEL_DIST) * 2.8;
            p.x += (dx / d) * f;
            p.y += (dy / d) * f;
          }
        }

        // particle–particle lines
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < LINE_DIST) {
            ctx.globalAlpha = (1 - d / LINE_DIST) * 0.18;
            ctx.strokeStyle = color;
            ctx.lineWidth   = 0.8;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }

        // particle dot
        ctx.globalAlpha = 0.5;
        ctx.fillStyle   = color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // cursor connections + glow dot
      if (mouse.x !== null && mouse.x >= 0 && mouse.x <= W &&
          mouse.y !== null && mouse.y >= 0 && mouse.y <= H) {

        for (const p of particles) {
          const d = Math.hypot(p.x - mouse.x, p.y - mouse.y);
          if (d < CURSOR_DIST) {
            ctx.globalAlpha = (1 - d / CURSOR_DIST) * 0.55;
            ctx.strokeStyle = color;
            ctx.lineWidth   = 1;
            ctx.beginPath();
            ctx.moveTo(p.x,     p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }

        ctx.globalAlpha = 0.65;
        ctx.fillStyle   = color;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };

    draw();

    const hero   = canvas.closest('.hero');
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => { mouse.x = null; mouse.y = null; };
    const onTouch = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.touches[0].clientX - r.left;
      mouse.y = e.touches[0].clientY - r.top;
    };

    if (hero) {
      hero.addEventListener('mousemove',  onMove);
      hero.addEventListener('mouseleave', onLeave);
      hero.addEventListener('touchmove',  onTouch, { passive: true });
      hero.addEventListener('touchend',   onLeave);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      if (hero) {
        hero.removeEventListener('mousemove',  onMove);
        hero.removeEventListener('mouseleave', onLeave);
        hero.removeEventListener('touchmove',  onTouch);
        hero.removeEventListener('touchend',   onLeave);
      }
    };
  }, [darkMode]);

  return <canvas ref={canvasRef} className="hero__canvas" />;
};

export default InteractiveParticles;
